/**
 * Funzioni di normalizzazione per il modulo flyers
 * @module flyers/utils/normalizers
 */

import { MULTI_AI_FIELDS } from "../constants/fields.js";
import {normalizeCanvasConfig} from "./canvasConfig.js";

/**
 * Normalizza un valore prezzo nel formato italiano (es. "12,99")
 * @param {any} val - Valore da normalizzare
 * @returns {string} Prezzo normalizzato o stringa vuota
 */
export const normalizePrice = (val) => {
    if (val == null) return "";
    let str = String(val).trim().replace(/\./g, ",").replace(/[^0-9,]/g, "");
    if (!str) return "";
    const [intPart, decPart = ""] = str.split(",");
    return `${intPart},${(decPart + "00").slice(0, 2)}`;
};

/**
 * Normalizza un path relativo rimuovendo il prefisso /media/flyers/
 * @param {any} value - Path da normalizzare
 * @returns {string} Path normalizzato
 */
export const normalizeRelPath = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "";
    if (raw.startsWith("/media/flyers/"))
        return raw.slice("/media/flyers/".length);
    if (raw.startsWith("media/flyers/"))
        return raw.slice("media/flyers/".length);
    if (/^https?:\/\//i.test(raw))
        return "";
    return raw;
};

const toPhotoUrl = (relPath, fallbackValue = "") => {
    const normalizedRel = normalizeRelPath(relPath);
    if (normalizedRel)
        return `/media/flyers/${normalizedRel}`;
    return String(fallbackValue || "").trim();
};

const normalizeProductPhotoItem = (item) => {
    const payload = item && typeof item === "object" ? item : {};
    const relPath = normalizeRelPath(payload.relPath || payload.url || payload.originalRelPath);
    const url = String(payload.url || toPhotoUrl(relPath)).trim();
    const originalRelPath = normalizeRelPath(payload.originalRelPath) || relPath || null;
    return {
        relPath,
        url,
        originalRelPath,
        composition: payload.composition ?? null,
        canvasConfig: payload.canvasConfig ? normalizeCanvasConfig(payload.canvasConfig) : null,
    };
};

export const normalizeProductPhotoItems = (ai) => {
    const source = Array.isArray(ai?.foto_prodotto_items) ? ai.foto_prodotto_items : [];
    const seen = new Set();
    const normalized = [];

    source.forEach((rawItem) => {
        const item = normalizeProductPhotoItem(rawItem);
        const key = item.relPath || item.url;
        if (!key || seen.has(key))
            return;
        seen.add(key);
        normalized.push(item);
    });

    if (!normalized.length) {
        const legacyUrl = String(ai?.foto_prodotto || "").trim();
        if (legacyUrl) {
            const legacyRel = normalizeRelPath(ai?.foto_prodotto_original) || normalizeRelPath(legacyUrl);
            normalized.push(
                normalizeProductPhotoItem({
                    relPath: legacyRel,
                    url: toPhotoUrl(legacyRel, legacyUrl),
                    originalRelPath: normalizeRelPath(ai?.foto_prodotto_original) || legacyRel || null,
                    composition: ai?.foto_prodotto_composition ?? null,
                    canvasConfig: ai?.foto_prodotto_canvasConfig ?? null,
                })
            );
        }
    }

    return normalized;
};

/**
 * Normalizza un valore in array di ID univoci
 * @param {any} value - Valore da normalizzare (singolo o array)
 * @returns {string[]} Array di ID univoci come stringhe
 */
export const normalizeIdArray = (value) => {
    if (value == null)
        return [];
    const list = Array.isArray(value) ? value : [value];
    const seen = new Set();
    return list.map((item) => (item == null ? "" : String(item).trim())).filter((item) => {
        if (!item) return false;
        if (seen.has(item)) return false;
        seen.add(item);
        return true;
    });
};

/**
 * Normalizza una card applicando normalizzazione ai campi array
 * @param {Object} card - Card da normalizzare
 * @returns {Object} Card normalizzata
 */
export const normalizeCard = (card) => {
    if (!card || typeof card !== "object")
        return card;
    const ai = card.ai || {};
    const normalizedAi = {
        ...ai,
        foto_prodotto_items: normalizeProductPhotoItems(ai),
        bollino: normalizeIdArray(ai.bollino),
        meccanica: normalizeIdArray(ai.meccanica),
        filtro: normalizeIdArray(ai.filtro)
    };
    return { ...card, ai: normalizedAi };
};

/**
 * Normalizza un valore AI in base alla chiave del campo
 * @param {string} key - Chiave del campo
 * @param {any} value - Valore da normalizzare
 * @returns {any} Valore normalizzato
 */
export const normalizeAiValue = (key, value) => {
    if (key === "categoria" || key === "classe" || key === "fondi")
        return value || null;
    if (MULTI_AI_FIELDS.has(key))
        return normalizeIdArray(value);
    return value;
};

/**
 * Normalizza un input in array (gestisce stringhe, array, valori nulli)
 * @param {any} input - Input da normalizzare
 * @returns {any[]} Array normalizzato
 */
export const normalizeToArray = (input) => {
    if (input == null) return [];
    if (Array.isArray(input)) return input;
    if (typeof input === "string") {
        const trimmed = input.trim();
        if (!trimmed) return [];
        return [trimmed];
    }
    return [input];
};

/**
 * Vero se la card è una gabbia banner: i suoi dati vivono in card.banner.*, non in card.ai.*
 * (scheda banner ed export XML leggono solo da lì).
 * @param {object} card - Card del volantino
 * @returns {boolean}
 */
export const isBannerCard = (card) => String(card?.typeGabbia || "").toLowerCase() === "banner";
